import {
    Component,
    OnInit,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnDestroy,
    Input,
    Output,
    EventEmitter
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UiUtilsService } from 'app/shared/utils/ui-utils.service';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subscription } from 'rxjs';
import { CustomerService } from 'app/shared/services/customer.service';
import { SharedService } from 'app/shared/services/shared.service';
import {
    activeInactiveLocationQueryParam,
    AppQueryParams,
    customerLocationGroupQueryParam,
    customerQueryParam,
    GetCustomerFeatureResponse,
    ProjectDetailsAdditionalFeature,
    SubscriptionLevel,
    SubscriptionTier,
    userSearchPageIndexParam,
    userSearchPageSizeParam,
    userSearchParam,
} from 'app/shared/models/customer';
import { CUSTOMER_SLIICER_FEATURE_ID } from 'app/shared/services/sliicer.service';
import { TrackBy } from 'app/shared/utils/track-by';

@Component({
    selector: 'app-customer-detail-feature',
    templateUrl: './customer-detail-feature.component.html',
    styleUrls: ['./customer-detail-feature.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDetailFeatureComponent implements OnInit, OnDestroy {
    @Input() public subscriptionLevel: number;
    @Input() public additionalFeaturesList: ProjectDetailsAdditionalFeature[] = [];
    @Output() public featureselected = new EventEmitter<boolean>();
    @Output() public featureSave = new EventEmitter<ProjectDetailsAdditionalFeature[]>();
    public subscriptionName: string = '';
    public adonsFeatureList: GetCustomerFeatureResponse[] = [];
    public selectedFeatureList: GetCustomerFeatureResponse[] = [];
    public allUserFeatureList: GetCustomerFeatureResponse[] = [];
    public allFeatureList: GetCustomerFeatureResponse[] = [];
    public customerFeatureLoadingState: boolean;
    public snackbarSuccessMsg: string;
    public snackbarErrMsg: string;
    public dismissBtnText: string;
    public isFeatureSelected: boolean = false;

    public SubscriptionLevel = SubscriptionLevel;

    private subscriptions: Subscription[] = [];
    private savedFeatureIds: number[] = [];

    translations = {
        noaccess: '',
        addons: ''
    }

    public trackById = TrackBy.byId;
    constructor(
        private activatedRoute: ActivatedRoute,
        private cdr: ChangeDetectorRef,
        private uiUtilsService: UiUtilsService,
        private sharedService: SharedService,
        private customerService: CustomerService,
        private translate: TranslateService,
        private router: Router
    ) {
        this.dismissBtnText = this.translate.instant('COMMON.DISMISS_TEXT');
        this.snackbarSuccessMsg = this.translate.instant('ADMIN.CUSTOMER_FEATURES.FEATURE_ADDED_SUCCESS_MESSAGE');
        this.snackbarErrMsg = this.translate.instant('ADMIN.CUSTOMER_FEATURES.FEATURE_EDIT_ERROR_MESSAGE');
        this.translations.addons = this.translate.instant('ADMIN.CUSTOMER_FEATURES.ADD_ONS');
        this.translations.noaccess = this.translate.instant('ADMIN.CUSTOMER_FEATURES.NO_ACCESS');
    }

    public ngOnInit() {
        // Leave timeout for other things to resolve first
        // Currently there is no way to load to this tab, so the delay makes no difference
        setTimeout(() => this.getCustomerFeatures(), 1000);
    }

    private updateFeatures(
        features: GetCustomerFeatureResponse[],
        allAvailableFeatures: GetCustomerFeatureResponse[],
        tiers: SubscriptionTier[],
    ): void {
        this.allUserFeatureList = features;
        this.allFeatureList = allAvailableFeatures;
        const currentSubscriptionTier = tiers?.find((v) => v.id === this.subscriptionLevel);
        this.selectedFeatureList = currentSubscriptionTier
            ? this.allFeatureList.filter(
                  (f) => typeof f.id === 'number' && currentSubscriptionTier.features.includes(f.id),
              )
            : [];

        this.subscriptionName = currentSubscriptionTier?.tier ?? '';

        const assignedFeaturesAssoc: Record<number, boolean> = {};

        for (const feature of this.additionalFeaturesList) {
            assignedFeaturesAssoc[feature.id] = true;
        }

        const sliicerFeature = allAvailableFeatures.find((feature) => feature.id === CUSTOMER_SLIICER_FEATURE_ID);
        this.adonsFeatureList = sliicerFeature ? [sliicerFeature] : [];

        for (const feature of this.adonsFeatureList) {
            feature.isChecked = typeof feature.id === 'number' && !!assignedFeaturesAssoc[feature.id];
        }

        this.savedFeatureIds = this.getSelectedFeatureIds();
        this.updateFeatureChangeState();
    }
    private getCustomerFeatures() {
        const featuresRequestSubs =
            combineLatest([
                this.sharedService.getFeatures(),
                this.sharedService.getAllFeatures(),
                this.customerService.getSubscriptionTiers()
            ])
        .subscribe(([response, allFeatures, tiers]) => {
            this.updateFeatures(response, allFeatures,  tiers);
            this.uiUtilsService.safeChangeDetection(this.cdr);
        });
        this.subscriptions.push(featuresRequestSubs);
    }

    /**
     * handle the feature list change event from UI
     * @param event
     * @param feature
     */
    public featureChangeHandler(event: MatCheckboxChange, selectedFeature: GetCustomerFeatureResponse): void {
        const customerFeat = this.adonsFeatureList.find((v) => v.id === selectedFeature.id);
        if (customerFeat) {
            customerFeat.isChecked = event.checked;
        }
        selectedFeature.isChecked = event.checked;
        this.updateFeatureChangeState();
    }


    /**
     * Function to setup the admin route in case of success
     */
    public setupAdminRoute() {
        const currentCustomerId = Number(this.activatedRoute.snapshot.queryParamMap.get(customerQueryParam));

        // get current query params for location group
        const locationGroupId = Number(this.activatedRoute.snapshot.queryParamMap.get(customerLocationGroupQueryParam));

        // get current query params for showing all locations
        const isShowAllLocations = Number(
            this.activatedRoute.snapshot.queryParamMap.get(activeInactiveLocationQueryParam),
        );

        // get search value
        const userSearchParamValue = this.activatedRoute.snapshot.queryParamMap.get(userSearchParam);

        // get search value
        const userSearchPageIndexParamValue = Number(
            this.activatedRoute.snapshot.queryParamMap.get(userSearchPageIndexParam),
        );

        // get search value
        const userSearchPageSizeParamValue = Number(
            this.activatedRoute.snapshot.queryParamMap.get(userSearchPageSizeParam),
        );

        // get global query params
        const appQueryParams:  AppQueryParams = {
            c: currentCustomerId,
            lg: locationGroupId || undefined,
            lt: isShowAllLocations || undefined,
            s: userSearchParamValue,
            pi: userSearchPageIndexParamValue,
            ps: userSearchPageSizeParamValue,
        };

        // Navigating with required params

        this.router.navigate(['/pages/customers'], {
            queryParams: appQueryParams,
            relativeTo: this.activatedRoute,
        });
    }
    /**
     * Update the customer feature association.
     */
    public saveCustomerAssignedFeatures(): void {
        this.featureSave.emit(this.getUpdatedAdditionalFeatures());
    }

    public getUpdatedAdditionalFeatures(): ProjectDetailsAdditionalFeature[] {
        return this.getSelectedFeatureIds().map((id: number) => ({ id }));
    }

    public hasPendingChanges(): boolean {
        return this.isFeatureSelected;
    }

    public markSaved(): void {
        this.savedFeatureIds = this.getSelectedFeatureIds();
        this.updateFeatureChangeState();
        this.uiUtilsService.safeChangeDetection(this.cdr);
    }

    private getSelectedFeatureIds(): number[] {
        if (this.subscriptionLevel === SubscriptionLevel.PrismLimited) {
            return [];
        }

        return this.adonsFeatureList
            .filter((feature: GetCustomerFeatureResponse) => feature.isChecked && typeof feature.id === 'number')
            .map((feature: GetCustomerFeatureResponse) => feature.id)
            .sort((firstId: number, secondId: number) => firstId - secondId);
    }

    private updateFeatureChangeState(): void {
        const selectedFeatureIds = this.getSelectedFeatureIds();
        this.isFeatureSelected = selectedFeatureIds.length !== this.savedFeatureIds.length
            || selectedFeatureIds.some((id: number, index: number) => id !== this.savedFeatureIds[index]);
        this.featureselected.emit(this.isFeatureSelected);
    }

    ngOnDestroy() {
        for(const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];
    }
}
